import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'app/shared/i18n';

const FloatingCard = ({ children, style, delay = '0s' }: { children: React.ReactNode; style?: React.CSSProperties; delay?: string }) => (
  <div
    style={{
      background: 'rgba(17,24,39,0.9)',
      border: '1px solid rgba(30,58,95,0.6)',
      borderRadius: 14,
      padding: '12px 16px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      animation: `heroFloat 6s ease-in-out infinite`,
      animationDelay: delay,
      ...style,
    }}
  >
    {children}
  </div>
);

const HeroIllustration = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        position: 'relative',
        width: 420,
        height: 400,
        flexShrink: 0,
      }}
    >
      {/* Central phone/screen shape */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 180,
          height: 260,
          borderRadius: 24,
          background: 'linear-gradient(135deg, #1E293B 0%, #111827 100%)',
          border: '2px solid rgba(34,197,94,0.3)',
          boxShadow: '0 0 60px rgba(34,197,94,0.1), 0 20px 60px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          zIndex: 2,
        }}
      >
        <div
          style={{
            fontSize: 36,
            fontWeight: 900,
            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Q
        </div>
        <div style={{ fontSize: 10, color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Q8Tools</div>
        {/* Inbox preview lines */}
        <div style={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {[0.9, 0.7, 0.5].map((o, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 6,
                  background: `rgba(34,197,94,${o * 0.2})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                }}
              >
                ✉️
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ height: 4, borderRadius: 2, background: `rgba(148,163,184,${o * 0.3})`, width: `${60 + i * 15}%` }} />
                <div
                  style={{
                    height: 3,
                    borderRadius: 2,
                    background: `rgba(148,163,184,${o * 0.15})`,
                    width: `${40 + i * 10}%`,
                    marginTop: 3,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating cards around the phone */}
      <FloatingCard style={{ position: 'absolute', top: 20, right: 20 }} delay="0s">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'rgba(16,185,129,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
            }}
          >
            ✓
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#10B981', fontFamily: "'JetBrains Mono', monospace" }}>+500</div>
            <div style={{ fontSize: 9, color: '#64748B' }}>{t('hero.floatSoldToday')}</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard style={{ position: 'absolute', top: 60, left: 0 }} delay="1s">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🛡️</span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#E2E8F0' }}>{t('hero.floatSecurity')}</div>
            <div style={{ fontSize: 9, color: '#64748B' }}>{t('hero.floatAutoDelete')}</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard style={{ position: 'absolute', bottom: 60, left: 10 }} delay="2s">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 16 }}>📧</span>
          <div style={{ fontSize: 10, color: '#22c55e', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>Hotmail Trusted</div>
        </div>
      </FloatingCard>

      <FloatingCard style={{ position: 'absolute', bottom: 20, right: 30 }} delay="0.5s">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'rgba(59,130,246,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
            }}
          >
            ⚡
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#3B82F6', fontFamily: "'JetBrains Mono', monospace" }}>34,761</div>
            <div style={{ fontSize: 9, color: '#64748B' }}>{t('hero.floatAccountsAvailable')}</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard style={{ position: 'absolute', top: 160, right: 0 }} delay="1.5s">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <span key={i} style={{ fontSize: 10, color: i <= 5 ? '#22c55e' : '#2a3a52' }}>
              ★
            </span>
          ))}
          <span style={{ fontSize: 9, color: '#94A3B8', marginLeft: 2 }}>5.0</span>
        </div>
      </FloatingCard>

      {/* Floating email icons */}
      {[
        { top: 10, left: 100, size: 20, delay: '0.3s', emoji: '✉️' },
        { top: 130, left: 30, size: 16, delay: '1.8s', emoji: '📨' },
        { top: 300, right: 80, size: 18, delay: '2.5s', emoji: '📬' },
      ].map((e, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: e.top,
            left: e.left,
            right: (e as any).right,
            fontSize: e.size,
            opacity: 0.4,
            animation: `heroFloat 5s ease-in-out infinite`,
            animationDelay: e.delay,
          }}
        >
          {e.emoji}
        </div>
      ))}
    </div>
  );
};

export const Hero = () => {
  const { t } = useTranslation();
  return (
    <section className="ms-hero">
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
      `}</style>

      {/* Glow orbs */}
      <div
        className="ms-hero__glow"
        style={{
          top: '-100px',
          left: '-100px',
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, #22c55e 0%, transparent 70%)',
        }}
      />
      <div
        className="ms-hero__glow"
        style={{ top: '50px', left: '70%', width: 300, height: 300, background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)' }}
      />

      <div className="ms-hero__inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 40 }}>
        {/* Left: Text content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="ms-badge ms-badge--success">🟢 {t('hero.systemOnline')}</span>

          <h1 className="ms-hero__title">
            {t('hero.title')} <span className="ms-hero__gradient-text">{t('hero.titleHighlight')}</span> {t('hero.titleSuffix')}
          </h1>

          <p className="ms-hero__desc">{t('hero.description')}</p>

          <div className="ms-hero__actions">
            <a href="#services" className="ms-btn ms-btn--primary" style={{ padding: '14px 28px', fontSize: 15 }}>
              {t('hero.viewProducts')} ↓
            </a>
            <Link to="/terms-of-service" className="ms-btn ms-btn--ghost" style={{ padding: '14px 28px', fontSize: 15 }}>
              {t('hero.terms')} →
            </Link>
          </div>

          <div className="ms-hero__stats">
            {[
              { label: t('hero.statProducts'), value: '50+', color: '#22c55e' },
              { label: t('hero.statSold'), value: '120K+', color: '#10B981' },
              { label: t('hero.statCustomers'), value: '8K+', color: '#3B82F6' },
            ].map((s, i) => (
              <div key={i} className="ms-stat" style={{ background: `${s.color}10`, border: `1px solid ${s.color}20` }}>
                <span className="ms-stat__value" style={{ color: s.color }}>
                  {s.value}
                </span>
                <span className="ms-stat__label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Animated illustration */}
        <div className="ms-hero__illustration">
          <HeroIllustration />
        </div>
      </div>
    </section>
  );
};
